import { GlobalNavigationContainer } from './global-navigation/GlobalNavigationContainer'
import { Outlet, ScrollRestoration } from 'react-router'
import { observer } from 'mobx-react-lite'
import { Suspense, useCallback, useState } from 'react'
import {
  NoticeBanner,
  NoticeBannerAction,
  NoticeBannerCloseButton,
  NoticeBannerText,
} from '@/features/shell/notice-banner/NoticeBanner'
import { LoadingPage } from '@/components/LoadingPage'
import { useElementSize } from '@/hooks/element-hooks'
import { PageTopOffsetContext } from '@/contexts/page-top-offset-context'
import { TermsOfUseAcceptanceDialogContainer } from '@/features/account/terms/TermsOfUseAcceptanceDialog/TermsOfUseAcceptanceDialogContainer'
import { DocumentDialogsHost } from '@/features/docs/DocumentDialogsHost'
import { TaxProfileDialogsHost } from '@/features/tax-profile/TaxProfileDialogsHost'
import { JobDialogsHost } from '@/features/jobs/JobDialogsHost'
import { HandoffDialogContainer } from '@/features/shell/handoff/HandoffDialog/HandoffDialogContainer'
import { Section7216AcceptanceDialogContainer } from '@/features/account/section-7216/Section7216AcceptanceDialog/Section7216AcceptanceDialogContainer'
import { useTranslate } from '@/hooks/translate'

/**
 * The ShellContainer
 */
export default observer(function ShellContainer() {
  const { connectRef: connectAnnouncementRef, size: announcementSize } =
    useElementSize()

  const {
    announcement,
    isShowingAnnouncement,
    closeAnnouncement,
    announcementLink,
  } = useAnnouncement()

  const pageTopOffset = announcementSize.height
  return (
    <GlobalNavigationContainer>
      {/* For now this just resets the scroll position on navigation */}
      <ScrollRestoration />

      {isShowingAnnouncement && (
        <NoticeBanner ref={connectAnnouncementRef}>
          <NoticeBannerText>
            {announcement}
            {announcementLink && (
              <NoticeBannerActionContainer
                label={announcementLink.label}
                url={announcementLink.url}
              />
            )}
          </NoticeBannerText>
          <NoticeBannerCloseButton onPress={closeAnnouncement} />
        </NoticeBanner>
      )}

      <PageTopOffsetContext value={pageTopOffset}>
        <Suspense fallback={<LoadingPage full />}>
          <Outlet />
        </Suspense>

        <Suspense fallback={<></>}>
          <HandoffDialogContainer />
          <TermsOfUseAcceptanceDialogContainer />
          <Section7216AcceptanceDialogContainer />
          <DocumentDialogsHost />
          <TaxProfileDialogsHost />
          <JobDialogsHost />
        </Suspense>
      </PageTopOffsetContext>
    </GlobalNavigationContainer>
  )
})

function useAnnouncement() {
  const T = useTranslate()
  const announcement = T('Web.Announcement', '').trim() || null
  const link = T('Web.Announcement.Link', '')
  const [isAnnouncementClosed, setIsAnnouncementClosed] = useState(false)
  const closeAnnouncement = useCallback(() => setIsAnnouncementClosed(true), [])
  const isShowingAnnouncement = !!announcement && !isAnnouncementClosed

  const splat = link.split('|')
  let announcementLink: { url: string; label: string } | null = null
  if (splat.length >= 2) {
    const label = splat[0].trim()
    const url = splat.slice(1).join('|').trim()
    if (label && url) {
      announcementLink = {
        label,
        url,
      }
    }
  }

  return {
    announcement,
    announcementLink,
    isShowingAnnouncement,
    closeAnnouncement,
  }
}

interface NoticeBannerActionContainerProps {
  label: string
  url: string
}

function NoticeBannerActionContainer({
  label,
  url,
}: NoticeBannerActionContainerProps) {
  if (/^https?:\/\//i.exec(url)) {
    return (
      <NoticeBannerAction target="_blank" href={url}>
        {label}
      </NoticeBannerAction>
    )
  }

  if (url.startsWith('/')) {
    return <NoticeBannerAction to={url}>{label}</NoticeBannerAction>
  }

  return null
}
